import React, { useEffect, useState } from 'react';
import API from '../api';
import { IcUsers, IcLogIn, IcLogOut, IcAlertTriangle, IcTrendUp, IcChevRight } from './Icons';

const StatCard = ({ label, value, icon: Icon, color, bg, sub, trend }) => (
  <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--slate-400)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--slate-900)', lineHeight: 1 }}>{value}</div>
      </div>
      <div style={{ width: 42, height: 42, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} stroke={color} strokeWidth={2} />
      </div>
    </div>
    {sub !== undefined && (
      <div style={{ fontSize: 12, color: 'var(--slate-400)', paddingTop: 10, borderTop: '1px solid var(--slate-100)' }}>
        {sub}
      </div>
    )}
  </div>
);

const EventRow = ({ e }) => {
  const name = e.student?.name || e.teacher?.name || e.unknown_person?.code || null;
  const sub  = e.student?.class || e.teacher?.subject || null;
  const isEntry = e.event_type === 'entry';
  const isUnknown = !e.student && !e.teacher;
  const time = new Date(e.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  return (
    <tr>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className={`avatar av-sm ${isUnknown ? 'av-red' : isEntry ? 'av-indigo' : 'av-orange'}`}>
            {isUnknown ? '?' : name?.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 500, color: 'var(--slate-800)', fontSize: 13 }}>
              {name || <span style={{ color: 'var(--red)', fontWeight: 600 }}>Inconnu</span>}
            </div>
            {sub && <div style={{ fontSize: 11, color: 'var(--slate-400)' }}>{sub}</div>}
          </div>
        </div>
      </td>
      <td>
        <span className={`badge ${isEntry ? 'badge-green' : 'badge-orange'}`}>
          {isEntry ? 'Entrée' : 'Sortie'}
        </span>
      </td>
      <td style={{ color: 'var(--slate-400)', fontSize: 12 }}>{time}</td>
      <td>
        {e.confidence ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ flex: 1, maxWidth: 60, height: 4, borderRadius: 999, background: 'var(--slate-200)' }}>
              <div style={{ height: '100%', borderRadius: 999, background: e.confidence > 0.8 ? 'var(--emerald)' : e.confidence > 0.6 ? 'var(--amber)' : 'var(--red)', width: `${Math.min(e.confidence * 100, 100)}%` }} />
            </div>
            <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>{(e.confidence * 100).toFixed(0)}%</span>
          </div>
        ) : '—'}
      </td>
    </tr>
  );
};

export default function Dashboard() {
  const [events, setEvents] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [unknowns, setUnknowns] = useState([]);
  const today = new Date().toISOString().split('T')[0];

  const load = () => {
    API.get(`/events/school/1?date_filter=${today}`).then(r => setEvents(r.data)).catch(() => {});
    API.get('/students/school/1').then(r => setStudents(r.data)).catch(() => {});
    API.get('/teachers/school/1').then(r => setTeachers(r.data)).catch(() => {});
    API.get('/unknown/school/1').then(r => setUnknowns(r.data)).catch(() => {});
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const entries  = events.filter(e => e.event_type === 'entry');
  const exits    = events.filter(e => e.event_type === 'exit');
  const alerts   = events.filter(e => !e.student && !e.teacher);
  const dateStr  = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ padding: 28 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 className="page-title">Tableau de bord</h1>
            <p className="page-subtitle" style={{ textTransform: 'capitalize' }}>{dateStr}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#fff', border: '1px solid var(--slate-200)', borderRadius: 8 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--emerald)' }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--slate-600)' }}>Système actif</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 24 }}>
        <StatCard label="Élèves enregistrés" value={students.length} icon={IcUsers} color="var(--indigo)" bg="var(--indigo-l)" sub={`${teachers.length} enseignant${teachers.length > 1 ? 's' : ''}`} />
        <StatCard label="Entrées aujourd'hui" value={entries.length} icon={IcLogIn} color="var(--emerald)" bg="var(--emerald-l)" sub="Accès validés" />
        <StatCard label="Sorties aujourd'hui" value={exits.length} icon={IcLogOut} color="var(--amber)" bg="var(--amber-l)" sub="Départs enregistrés" />
        <StatCard label="Alertes inconnus" value={alerts.length} icon={IcAlertTriangle} color="var(--red)" bg="var(--red-l)" sub={`${unknowns.length} inconnu${unknowns.length > 1 ? 's' : ''} en base`} />
      </div>

      {/* Table événements */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--slate-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--slate-800)' }}>Activité du jour</div>
            <div style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 1 }}>{events.length} événement{events.length > 1 ? 's' : ''}</div>
          </div>
        </div>
        {events.length === 0 ? (
          <div className="empty">
            <IcClock size={40} stroke="var(--slate-300)" />
            <p style={{ marginTop: 12 }}>Aucun événement enregistré aujourd'hui</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Personne</th>
                <th>Type</th>
                <th>Heure</th>
                <th>Confiance</th>
              </tr>
            </thead>
            <tbody>
              {events.slice(0, 12).map(e => <EventRow key={e.id} e={e} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function IcClock({ size, stroke }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20" /><path d="M12 6v6l4 2" /></svg>;
}
